import _ from 'lodash';
import { authenticatedInstance } from 'api';
import { triggerToast } from 'redux/actions/ui';
import { updateNDOrderHistory } from 'redux/actions/NDWalletData';

export const INITIALIZE_OPEN_ORDERS = 'INITIALIZE_OPEN_ORDERS';
export const UPDATE_OPEN_ORDERS = 'UPDATE_OPEN_ORDERS';
export const SET_PERCENT_AMOUNT = 'SET_PERCENT_AMOUNT';
export const CANCEL_ORDER_PROCESS_STARTED = 'CANCEL_ORDER_PROCESS_STARTED';
export const CANCEL_ORDER_PROCESS_FINISHED = 'CANCEL_ORDER_PROCESS_FINISHED';
export const NEW_ORDER_HISTORY_LOADING_STARTED = 'NEW_ORDER_HISTORY_LOADING_STARTED';
export const NEW_ORDER_HISTORY_LOADING_FINISHED = 'NEW_ORDER_HISTORY_LOADING_FINISHED';
export const NEW_ORDER_HISTORY_LOADED = 'NEW_ORDER_HISTORY_LOADED';
export const MY_TRADE_HISTORY_LOADING_STARTED = 'MY_TRADE_HISTORY_LOADING_STARTED';
export const MY_TRADE_HISTORY_LOADING_FINISHED = 'MY_TRADE_HISTORY_LOADING_FINISHED';
export const MY_TRADE_HISTORY_LOADED = 'MY_TRADE_HISTORY_LOADED';

export const LOAD_ORDER_HISTORY = {
  STARTED: 'LOAD_ORDER_HISTORY_STARTED',
  SUCCESS: 'LOAD_ORDER_HISTORY_SUCCESS',
  FAIL: 'LOAD_ORDER_HISTORY_FAIL',
};

export const LOAD_TRADE_HISTORY = {
  STARTED: 'LOAD_TRADE_HISTORY_STARTED',
  SUCCESS: 'LOAD_TRADE_HISTORY_SUCCESS',
  FAIL: 'LOAD_TRADE_HISTORY_FAIL',
};

const formatOpenOrder = (order) => {
  const { order_id, base, quote, pending, volume, rate, stop_price, type, side, timestamp, status, order_status } = order;

  return {
    orderId: order_id,
    base,
    quote,
    tradingPair: `${base}/${quote}`,
    volume: volume,
    pendingVolume: pending,
    price: rate,
    stopPrice: stop_price,
    orderType: type,
    orderSide: side,
    orderState: status,
    orderStatus: order_status,
    date: timestamp,
    // moment
    //   .utc(timestamp)
    //   .local()
    //   .format('L HH:mm'),
  };
};

export const updateOpenOrders = (order) => {
  const formattedOrder = formatOpenOrder(order.Data);

  return (dispatch, getState) => {
    const openOrders = getState().orders.openOrders.slice(0);
    const index = _.findIndex(openOrders, ['orderId', formattedOrder.orderId]);

    if (index >= 0) {
      if (formattedOrder.orderState) {
        openOrders.splice(index, 1);
      } else {
        openOrders[index] = formattedOrder;
      }
    } else {
      if (!formattedOrder.orderState) {
        openOrders.unshift(formattedOrder);
      }
    }

    dispatch({
      type: UPDATE_OPEN_ORDERS,
      payload: openOrders,
    });
  };
};

// Replace the open-orders state with the given snapshot. The backend pushes
// either a full list (on subscribe / pair switch) or a single-order delta
// wrapped in an array; both cases are handled by simply formatting each
// entry and filtering out anything whose status marks it completed or
// cancelled (`orderState` truthy). The previous implementation only
// processed `orders[0]` when existing state was non-empty, which dropped
// every order but the first on reconnect / re-subscribe.
export const initializeOpenOrders = (orders) => {
  return (dispatch) => {
    const formatted = orders.map(formatOpenOrder).filter((o) => !o.orderState);

    dispatch({
      type: INITIALIZE_OPEN_ORDERS,
      payload: formatted,
    });
  };
};

export const cancelOrder = (order) => {
  return async (dispatch) => {
    try {
      dispatch({ type: CANCEL_ORDER_PROCESS_STARTED, payload: order.orderId });

      const { data } = await authenticatedInstance({
        url: '/api/CancelOrder',
        method: 'POST',
        data: {
          side: order.orderSide.toUpperCase(),
          pair: order.tradingPair.replace('/', '_'),
          orderId: order.orderId,
        },
      });

      if (data.status === 'Success') {
        dispatch(triggerToast('orderCancelled', 'success'));
        // dispatch(getNDWalletBalance());
        dispatch(updateNDOrderHistory());
        dispatch(getNewOrderHistory());
      } else {
        dispatch(triggerToast(data.message, 'error'));
      }

      dispatch({ type: CANCEL_ORDER_PROCESS_FINISHED });
    } catch (e) {}
  };
};

const handlePlaceOrder = _.debounce(
  async (dispatch, orderData) => {
    // Format any numbers to strings
    const formattedData = {};
    Object.entries(orderData).forEach(([key, value]) => {
      if (_.includes(['rate', 'volume', 'stop', 'trail'], key)) {
        formattedData[key] = value.toString();
      } else {
        formattedData[key] = value;
      }
    });

    try {
      const { data } = await authenticatedInstance({
        url: 'api/PlaceOrder',
        method: 'POST',
        data: { ...formattedData },
      });

      if (data.status === 'Success') {
        const hasSuccess = _.includes(['MSG_ORDER_ACCEPTED', 'Success_General'], data?.message);
        dispatch(triggerToast(data?.message, hasSuccess ? 'success' : 'error'));
        // dispatch(getNDWalletBalance());
        dispatch(updateNDOrderHistory());
      } else {
        if (_.isEqual(data.message, 'OKX_Failure')) {
          dispatch(triggerToast(data.data, 'error'));
        } else {
          dispatch(triggerToast(data.message, 'error'));
        }
      }
    } catch (e) {
      console.log(e);
    }
  },
  1500,
  { leading: true, trailing: false },
);

const handlePlaceOrderPriced = _.debounce(
  async (dispatch, orderData) => {
    // Format any numbers to strings
    const formattedData = {};
    Object.entries(orderData).forEach(([key, value]) => {
      if (_.includes(['rate', 'stop', 'trail'], key)) {
        formattedData[key] = value.toString();
      } else {
        formattedData[key] = value;
      }
    });
    formattedData['amount'] = parseFloat(formattedData.volume);
    delete formattedData.type;
    delete formattedData.volume;

    try {
      const { data } = await authenticatedInstance({
        url: 'api/PlaceOrder_Priced',
        method: 'POST',
        data: { ...formattedData },
      });

      if (data.status === 'Success') {
        const hasSuccess = _.includes(['MSG_ORDER_ACCEPTED', 'Success_General'], data?.message);
        dispatch(triggerToast(data?.message, hasSuccess ? 'success' : 'error'));
        // dispatch(getNDWalletBalance());
        dispatch(updateNDOrderHistory());
      } else {
        if (_.isEqual(data.message, 'OKX_Failure')) {
          dispatch(triggerToast(data.data, 'error'));
        } else {
          dispatch(triggerToast(data.message, 'error'));
        }
      }
    } catch (e) {
      console.log(e);
    }
  },
  1500,
  { leading: true, trailing: false },
);

export const placeOrder = (orderData) => {
  delete orderData.total;

  return (dispatch) => handlePlaceOrder(dispatch, orderData);
};

export const placeOrderPriced = (orderData) => {
  delete orderData.total;

  return (dispatch) => handlePlaceOrderPriced(dispatch, orderData);
};

const handleGetTradeHistory = _.debounce(async (dispatch, tradingPair) => {
  if (tradingPair !== 'ALL') {
    tradingPair = `${tradingPair.quoteCurrency}-${tradingPair.baseCurrency}`;
  }
  try {
    dispatch({
      type: LOAD_TRADE_HISTORY.STARTED,
    });

    const { data } = await authenticatedInstance({
      url: '/api/TradeHistory',
      method: 'GET',
      data: {
        side: 'ALL',
        pair: tradingPair,
        count: 100,
        page: 1,
      },
      polling: true,
    });

    if (data.status === 'Success') {
      dispatch({
        type: LOAD_TRADE_HISTORY.SUCCESS,
        payload: data.data,
      });
    }
  } catch (e) {
    dispatch({
      type: LOAD_TRADE_HISTORY.FAIL,
    });
    // dispatch(triggerToast('somethingWentWrong'));
  }
}, 1500);

// By default will fetch all trade history, for specific trading pairs `tradingPair` should
// be the standard tradingPair shape across the app.
export const getTradeHistory = (tradingPair = 'ALL') => {
  return (dispatch) => handleGetTradeHistory(dispatch, tradingPair);
};

const formatUserOrderHistory = (tradeHistory) =>
  tradeHistory.map(({ currencyPair, ...rest }) => {
    const [trade, market] = currencyPair.split('-');

    return {
      market,
      trade,
      ...rest,
    };
  });

export const getOrderHistory = (tradingPair = 'ALL') => {
  if (tradingPair !== 'ALL') {
    tradingPair = `${tradingPair.quoteCurrency}-${tradingPair.baseCurrency}`;
  }

  return async (dispatch) => {
    try {
      dispatch({
        type: LOAD_ORDER_HISTORY.STARTED,
      });

      const { data } = await authenticatedInstance({
        url: '/api/TradeHistory_Grouped',
        method: 'GET',
        data: {
          side: 'ALL',
          pair: tradingPair,
        },
        polling: true,
      });

      if (data.status === 'Success') {
        dispatch({
          type: LOAD_ORDER_HISTORY.SUCCESS,
          payload: formatUserOrderHistory(data.data),
        });
      }
    } catch (e) {
      dispatch({
        type: LOAD_ORDER_HISTORY.FAIL,
      });
      // dispatch(triggerToast('somethingWentWrong'));
    }
  };
};

export const getNewOrderHistory = (page = 1, limit = 25) => {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: NEW_ORDER_HISTORY_LOADING_STARTED,
      });

      // Derive the current pair from exchange state instead of hardcoding.
      // The /api/get-order-history endpoint expects the pair in
      // MARKET_CURRENCY (= quote_base) format with an underscore
      // separator — the backend parses it as
      //   market = pair.Split('_')[0]   // quote currency
      //   currency = pair.Split('_')[1] // base currency
      // and then filters .Where(x => x.Currency == currency && x.Market == market),
      // where in the OrderCache x.Currency is the traded coin (base)
      // and x.Market is the market coin (quote). So for ETH-BTC we must
      // send "BTC_ETH", not "ETH_BTC".
      const { tradingPair } = getState().exchange;
      let pair = 'ALL';
      if (tradingPair && tradingPair.baseCurrency && tradingPair.quoteCurrency) {
        pair = `${tradingPair.quoteCurrency}_${tradingPair.baseCurrency}`;
      } else if (typeof tradingPair === 'string' && tradingPair) {
        pair = tradingPair;
      }

      const response = await authenticatedInstance({
        url: '/api/get-order-history',
        method: 'POST',
        data: {
          side: 'ALL',
          orderID: -1,
          openOrders: false,
          pair,
          page,
          pageSize: limit,
        },
      });

      if (response.data.status === 'Success') {
        dispatch({
          type: NEW_ORDER_HISTORY_LOADED,
          payload: {
            items: response.data.data.items,
            currentPage: response.data.data.currentPage,
            limit: response.data.data.pageSize,
            pagesAmount: Math.ceil(response.data.data.totalCount / response.data.data.pageSize),
          },
        });
      }
    } finally {
      dispatch({
        type: NEW_ORDER_HISTORY_LOADING_FINISHED,
      });
    }
  };
};

// Fetches the authenticated user's matched trades (executions) from
// /api/TradeHistory. Unlike get-order-history which returns ORDERS with
// nested mOrders, this endpoint returns one row per matched trade.
//
// The backend (ExchangeController.cs OrderController.TradeHistory) expects
// the pair as BASE-QUOTE with a DASH separator — note it filters by
//   (x.Currency + "-" + x.Market == currencyPair)
// where x.Currency is the base coin and x.Market is the quote coin, so for
// ETH-BTC we send "ETH-BTC".
export const getMyTradeHistory = (page = 1, count = 25) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: MY_TRADE_HISTORY_LOADING_STARTED });

      const { tradingPair } = getState().exchange;
      let pair = 'ALL';
      if (tradingPair && tradingPair.baseCurrency && tradingPair.quoteCurrency) {
        pair = `${tradingPair.baseCurrency}-${tradingPair.quoteCurrency}`;
      }

      const response = await authenticatedInstance({
        url: '/api/TradeHistory',
        method: 'GET',
        data: {
          side: 'ALL',
          pair,
          page,
          count,
        },
      });

      if (response.data.status === 'Success') {
        const payload = response.data.data || {};
        const pageInfo = payload.pageInfo || {};
        const rows = payload.rows || [];
        dispatch({
          type: MY_TRADE_HISTORY_LOADED,
          payload: {
            rows,
            currentPage: pageInfo.currentPage || page,
            limit: pageInfo.pageSize || count,
            pagesAmount: Math.max(1, Math.ceil((pageInfo.totalRows || 0) / (pageInfo.pageSize || count))),
          },
        });
      }
    } finally {
      dispatch({ type: MY_TRADE_HISTORY_LOADING_FINISHED });
    }
  };
};

export const setAmount = (side, percentSelected, marketOrder) => {
  return (dispatch, getState) => {
    const { portfolios } = getState().portfolio;
    const {
      tradingPair: { baseCurrency, quoteCurrency },
      orderBookAsk,
    } = getState().exchange;

    let lastPrice;
    let totalValue;
    let currency = side === 'BUY' ? quoteCurrency : baseCurrency;
    const balance = _.get(portfolios, `${currency}.balance`, 0);

    if (side === 'BUY') {
      if (!_.isEmpty(orderBookAsk)) {
        orderBookAsk.sort((a, b) => a.price - b.price);
        let roundTotal = 0;
        orderBookAsk.forEach((element) => {
          element.total = element.volume * element.price;
          if (roundTotal === 0) {
            element.roundTotal = roundTotal + element.total;
            roundTotal = element.roundTotal;
          } else {
            if (roundTotal < percentSelected * balance) {
              element.roundTotal = roundTotal + element.total;
              roundTotal = element.roundTotal;

              if (roundTotal >= percentSelected * balance) {
                lastPrice = element.price;
                return;
              } else {
                lastPrice = element.price;
              }
            }
          }
        });
      }
    }

    let amount = side === 'BUY' ? (percentSelected * balance) / lastPrice : percentSelected * balance;

    amount = amount === Infinity ? 0 : amount;

    if (side === 'BUY') {
      totalValue = percentSelected * balance;
      totalValue = totalValue === Infinity ? 0 : totalValue;
    }

    dispatch({
      type: SET_PERCENT_AMOUNT,
      payload: {
        side: `${side.toLowerCase()}Value`,
        amount,
        totalValue,
      },
    });
  };
};
